// ============================================================
// SPINLOOP v3 — Type definitions
// ============================================================

export type Sport = "pickleball" | "padel" | "tennis" | "squash";
// organizer = järjestäjä: kutsut, ottelut, tulokset ja illan päättäminen
// (docs/roolit.md). Myönnetään vain varmennetulla kirjautumisella.
export type Role  = "super_admin" | "club_admin" | "organizer" | "player";

// ── Club ────────────────────────────────────────────────────
export interface Club {
  id: string;
  name: string;
  clubCode: string;        // esim. "JURMOZ"
  // PIN ei ole klubidokumentissa vaan alikokoelmassa clubs/{id}/private/auth,
  // jota vain palvelin ja super admin saavat lukea. Taalla vain siksi, etta
  // super adminin nakymassa se naytetaan erikseen haettuna.
  adminPin?: string;
  logoUrl?: string;
  licenseActive?: boolean;
  createdAt: string;
  // Yhteystiedot
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  // Väriteemat
  primaryColor?: string;   // esim. "#163b68"
  secondaryColor?: string; // esim. "#f4a426"
  // MobilePay Lipas -koodit. Nämä olivat aiemmin vain selaimen
  // localStoragessa, jolloin pelaaja ei nähnyt maksunappia koskaan: hän ei
  // ollut kirjoittanut niitä omaan selaimeensa. Klubidokumentissa ne ovat
  // kaikkien klubin jäsenten luettavissa, mikä on koko ominaisuuden ehto.
  lipasSali?: string;      // salivuoron maksu
  lipasPanos?: string;     // pelipanoksen maksu
  // Klubin pelipaikat. Ylläpitäjä määrittää kerran nimen ja osoitteen,
  // ja tapahtumaan paikka valitaan listasta. Osoite kulkee tapahtuman
  // mukana kalenteriin ja kutsuihin, jotta puhelin osaa navigoida.
  venues?: Venue[];
  // Montako tulevaa tapahtumaa ylläpitäjän lista näyttää ennen
  // "Näytä kaikki" -nappia. Liigan 12 iltaa teki listasta pitkän.
  eventListLimit?: number;
}

export interface Venue {
  name: string;            // esim. "Pickleball-halli Vantaa"
  address: string;         // esim. "Tiilitie 5, 01720 Vantaa"
  // Kentän päiden nimet, jos ne eivät ole samanarvoisia — esimerkiksi
  // kun toisen takakentän takana on seinä lähellä. Americano tasaa
  // puolet joka tapauksessa (`balanceSides`), mutta ilman nimiä pelaaja
  // ei tiedä kumpaan päätyyn mennään, ja tasaus jää kirjanpidoksi.
  // `sideA` on `team1`:n pää. Molemmat tai ei kumpaakaan.
  sideA?: string;          // esim. "Seinän puoli"
  sideB?: string;          // esim. "Ikkunan puoli"
}

// ── Season (clubs/{clubId}/seasons/{seasonId}) ───────────────
//
// Liiga eli kausisarja: kerros tavallisten tapahtumien päälle. Tapahtuma
// kuuluu kauteen, jos sillä on seasonId. Taulukkoa ei tallenneta vaan se
// lasketaan tapahtumien sijoituksista (Participant.finalPosition) näillä
// säännöillä — säännön muuttaminen kesken kauden ei vaadi datan korjausta.
// Suunnitelma: docs/liiga.md.
export interface SeasonRules {
  /** Montako parasta tapahtumaa lasketaan. Poissaolo ei pudota pelistä. */
  countBest: number;
  /** Vähimmäismäärä pelattuja tapahtumia, jotta sijoitus oikeuttaa palkintoon. */
  minEvents: number;
  /**
   * **Käyttämätön 2.11.2026 jälkeen.** Sijoituspisteet illan
   * lopputuloksesta: indeksi 0 = 1. sija.
   *
   * Kausi lasketaan nyt tehdyistä pisteistä (`utils/league.ts`), ei
   * sijoituksesta. Kenttä on jätetty tyyppiin, koska se on kannassa
   * kaikilla olemassa olevilla kausilla eikä sitä kannata poistaa
   * migraatiolla: lukematta jäävä kenttä ei riko mitään. Älä lisää sille
   * uutta lukijaa — jos asteikko joskus palaa, se on oma päätös ja
   * dokumentoidaan `docs/liiga.md`:ssä.
   */
  placementPoints: number[];
  /** **Käyttämätön 2.11.2026 jälkeen.** Kiinteä piste pelatusta illasta. */
  attendancePoints: number;
}

export interface Season {
  id: string;
  name: string;            // "Syysliiga 2026"
  startsAt: string;        // ISO, ensimmäinen tapahtuma
  endsAt: string;          // ISO, viimeinen tapahtuma
  status: "open" | "finished";
  rules: SeasonRules;
  createdAt: string;
}

export const DEFAULT_SEASON_RULES: SeasonRules = {
  countBest: 8,
  minEvents: 5,
  placementPoints: [20, 17, 15, 13, 12, 11, 10, 9, 8, 7, 6, 5],
  attendancePoints: 5,
};

// ── Player (clubs/{clubId}/players/{playerId}) ───────────────
export interface Player {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  // Pelaajan oma profiilikuva. Skaalataan selaimessa neliöksi ennen
  // lähetystä, joten tämä osoittaa pieneen JPEGiin eikä puhelimen
  // alkuperäiseen kuvaan. Henkilötietoa — katso PrivacyPage.
  photoUrl?: string;
  duprId?: string;              // DUPR-käyttäjätunnus linkitystä varten
  pprId?: string;
  utrId?: string;
  passkeyCredId?: string;
  // Milloin pelaaja kavi tervetulovaiheen lapi. Puuttuu vanhoilta
  // pelaajilta, joille se nayttaytyy kerran seuraavalla avauksella.
  onboardedAt?: string;
  ratings: PlayerRatings;
  // DUPR API -luvut (haetaan automaattisesti match submission jälkeen)
  duprDoublesRating?: number;
  duprSinglesRating?: number;
  duprRefreshedAt?: string;
  // Milloin pelaaja yhdisti DUPR-tilinsä itse (duprExchangeCode). Puuttuu,
  // jos vain tunnus on syötetty: silloin DUPR ei luovuta ratingia eikä
  // ottelut mene DUPR:iin, ja kortin pitää sanoa se.
  duprLinkedAt?: string;
  // DUPR User Gating
  duprHasBasicL1?: boolean;
  duprEntitlements?: string[];
  // Spinloop-tilastot (päivittyy tapahtuman sulkemisessa)
  wins?: number;
  losses?: number;
  lastSeen?: string;
  createdAt: string;
}

export interface PlayerRatings {
  pickleball?: number;   // Manuaalisesti syötetty DUPR-luku (ei ylikirjoiteta automaattisesti)
  padel?: number;        // PPR   1.0–10.0
  tennis?: number;       // UTR   1.0–16.0
}

export type RatingLevel = {
  label: string;
  color: string;
};

// ── Event (clubs/{clubId}/events/{eventId}) ─────────────────
export interface ClubEvent {
  id: string;
  sport: Sport;
  title?: string;
  datetime: string;
  venue?: string;
  // Paikan katuosoite. Tallennetaan tapahtumaan eikä haeta klubin
  // listasta, jotta vanhat tapahtumat säilyvät ennallaan vaikka
  // paikkalista muuttuisi. Puuttuu vanhoista tapahtumista.
  venueAddress?: string;
  // Kausi, johon tapahtuma kuuluu. Puuttuu tavallisilta pelivuoroilta.
  seasonId?: string;
  pin: string;
  panos?: number;
  lipasUrl?: string;
  gameType: "Americano" | "RoundRobin" | "Manuaalinen";
  status: "open" | "active" | "finished";
  createdAt: string;
  // DUPR-integraatio
  duprSubmitted?: boolean;
  duprSubmittedAt?: string;
  // Kutsumuistutus. invitesSentAt kirjoitetaan palvelimella kun kutsut on
  // oikeasti lahetetty; nudgedAt estaa saman muistutuksen toistumisen.
  invitesSentAt?: string;
  nudgedAt?: string;
  // Muistutus kutsutuille jotka eivat ole vastanneet (remindInvitedPlayers,
  // functions/src/inviteReminder.ts). Kaikki kolme kirjoittaa palvelin.
  invitedNames?: string[];
  playersRemindedAt?: string;
  playersReminded?: number;
  // Viestiketjun asetus (docs/ideat.md G1). Puuttuu = "verified".
  messages?: MessagesMode;
}

/**
 * Kuka saa kirjoittaa tapahtuman viesteihin: "off" ei kukaan, "verified"
 * varmennetusti kirjautuneet ja ylläpito, "all" myös PIN-kirjautuneet
 * (viestit merkitään vahvistamattomiksi).
 */
export type MessagesMode = "off" | "verified" | "all";

// ── Participant (clubs/{clubId}/events/{eventId}/participants/{id}) ──
export interface Participant {
  id: string;
  playerId?: string;      // viittaus players-kokoelmaan
  guestName?: string;     // vieraat joilla ei profiilia
  displayName: string;
  confirmed: boolean;
  paid: boolean;
  /**
   * Kuka kirjoitti rivin viimeksi: "player" = pelaaja itse (ilmoittautui,
   * perui tai vastasi kutsuun), "admin" = ylläpitäjä. Puuttuu vanhoilta
   * ja ylläpitäjän lisäämiltä riveiltä. Erottaa kutsutun joka ei ole
   * vastannut siitä joka sanoi "en pääse" (utils/invite.ts).
   */
  source?: "player" | "admin";
  seedRating?: number;
  finalPosition?: number;
  /**
   * Oliko pelaaja illassa paikalla. Ylläpitäjä merkitsee sen tapahtuma-
   * kortissa (Paikalla?), ja illan päättäminen lisää sen jokaiselle joka
   * pelasi (nightResults, src/utils/closeEvent.ts). Ennen 10/2026 tämä
   * kirjattiin kaikille vahvistetuille, jolloin tulematta jäänytkin sai
   * pisteen. Liigan läsnäolopiste tulee tästä, ei sijoituksesta: sijoitus
   * on vain pelanneilla, mutta +5 kuuluu paikalle tulemisesta.
   */
  attended?: boolean;
}

// ── Game (clubs/{clubId}/events/{eventId}/games/{id}) ───────
export interface Game {
  id: string;
  round?: number;
  court?: string;
  team1: string[];
  team2: string[];
  score1?: number;
  score2?: number;
  playedAt?: string;
  isExtra?: boolean;
  // DUPR-integraatio
  duprSubmittedAt?: string;
  duprMatchCode?: string;
  duprMatchId?: number;
  duprDeletedAt?: string;
}

// ── Auth session ─────────────────────────────────────────────
export interface AuthSession {
  role: Role;
  clubId: string;
  clubCode: string;
  clubName: string;
  playerId?: string;
  playerName?: string;
  eventId?: string;
}

// ── Rating calculations ──────────────────────────────────────
export function calcDUPR(wins: number, losses: number): number {
  const raw = 3.0 + wins * 0.075 - losses * 0.05;
  return Math.min(8.0, Math.max(2.0, parseFloat(raw.toFixed(2))));
}

export function calcPPR(wins: number, losses: number): number {
  const raw = 3.0 + wins * 0.08 - losses * 0.05;
  return Math.min(10.0, Math.max(1.0, parseFloat(raw.toFixed(2))));
}

export function calcUTR(wins: number, losses: number): number {
  const raw = 4.0 + wins * 0.12 - losses * 0.07;
  return Math.min(16.0, Math.max(1.0, parseFloat(raw.toFixed(2))));
}

export function duprLevel(r: number): RatingLevel {
  if (r >= 7.0) return { label: "Huippu",       color: "#f59e0b" };
  if (r >= 6.0) return { label: "Kilpapelaaja", color: "#3b82f6" };
  if (r >= 5.0) return { label: "Edistynyt",    color: "#8b5cf6" };
  if (r >= 4.0) return { label: "Kehittyvä",    color: "#10b981" };
  if (r >= 3.0) return { label: "Harrastaja",   color: "#6b7280" };
  return               { label: "Aloittelija",  color: "#9ca3af" };
}

export function pprLevel(r: number): RatingLevel {
  if (r >= 9.0) return { label: "Pro",          color: "#f59e0b" };
  if (r >= 7.0) return { label: "Kilpapelaaja", color: "#3b82f6" };
  if (r >= 5.0) return { label: "Kehittyvä",    color: "#10b981" };
  if (r >= 3.0) return { label: "Harrastaja",   color: "#6b7280" };
  return               { label: "Beginner",     color: "#9ca3af" };
}

export function utrLevel(r: number): RatingLevel {
  if (r >= 14.0) return { label: "Elite",    color: "#f59e0b" };
  if (r >= 12.0) return { label: "National", color: "#3b82f6" };
  if (r >= 8.0)  return { label: "Regional", color: "#8b5cf6" };
  if (r >= 4.0)  return { label: "Club",     color: "#10b981" };
  return                { label: "Beginner", color: "#9ca3af" };
}

export function getRatingInfo(sport: Sport, wins: number, losses: number): {
  value: number; label: string; color: string; max: number; name: string;
} {
  if (sport === "pickleball") {
    const v = calcDUPR(wins, losses);
    return { value: v, ...duprLevel(v), max: 8, name: "DUPR" };
  }
  if (sport === "padel") {
    const v = calcPPR(wins, losses);
    return { value: v, ...pprLevel(v), max: 10, name: "PPR" };
  }
  if (sport === "tennis") {
    const v = calcUTR(wins, losses);
    return { value: v, ...utrLevel(v), max: 16, name: "UTR" };
  }
  const v = calcDUPR(wins, losses);
  return { value: v, ...duprLevel(v), max: 8, name: "Rating" };
}

// ── Sijoitukset ──────────────────────────────────────────────
// Yksi totuus tulostaulukolle. Admin-näkymä ja pelaajan historia
// laskivat sijoituksen ennen erikseen, ja ne olivat eri mieltä:
// historia järjesti pelkän voittomäärän mukaan ilman ratkaisua
// tasatilanteeseen, joten kolmen pelaajan tasapeli ratkesi siinä
// Firestoren dokumenttijärjestyksen mukaan. Sama pelaaja näkyi
// adminille kultana ja itselleen pronssina.
//
// Järjestys: voitot, sitten tehdyt pisteet — sama kuin ennenkin
// admin-puolella, koska se on se mitä tapahtumassa luetaan ääneen.
export interface PlayerStanding {
  name: string;
  wins: number;
  losses: number;
  pts: number;
  ptsAgainst: number;
  /** Pelatut pelit, tasapelit mukaan lukien. 0 = ei pelannut illassa. */
  played: number;
}

/**
 * Yhden pelin tulos. Ainoa paikka, jossa päätetään mikä peli lasketaan.
 *
 * - Tuloksen puuttuminen tai 0–0 tarkoittaa pelaamatonta peliä. Kierroksen
 *   pelit luodaan valmiiksi, ja 0–0 jää kenttään, jos kierrosta ei ehditty
 *   pelata tai tulosta ei kirjattu. Aiemmin 0–0 laskettiin voitoksi
 *   oikeanpuoleiselle parille (0 > 0 on epätosi), ja ensimmäisessä
 *   liigaillassa se antoi kahdelle pelaajalle voiton pelistä jota ei pelattu.
 * - Tasatulos (esim. 9–9, aika loppui kesken) ei ole kummankaan voitto.
 *   Pisteet lasketaan molemmille, voittoa tai tappiota ei. Aiemmin tasapeli
 *   meni sekin oikeanpuoleiselle parille.
 *
 * Profiilin tilastot ohittivat 0–0:n jo ennestään; nyt sama sääntö pätee
 * kaikkialla.
 */
export type GameResult = "unplayed" | "team1" | "team2" | "tie";
export function gameResult(g: Pick<Game, "score1" | "score2">): GameResult {
  if (g.score1 == null || g.score2 == null) return "unplayed";
  if (g.score1 === 0 && g.score2 === 0) return "unplayed";
  if (g.score1 === g.score2) return "tie";
  return g.score1 > g.score2 ? "team1" : "team2";
}

/**
 * Sijoitukset jarjestetylle taulukolle niin, etta tasapisteet jakavat
 * saman sijan: 1. 1. 3. eika 1. 2. 3.
 *
 * Ilman tata illan sija tuli taulukon jarjestyksesta, ja jarjestys tuli
 * tasatilanteessa Object.values-jarjestyksesta eli Firestoren
 * dokumenttijarjestyksesta. Saman parin pelaajat — samat voitot, samat
 * pisteet — saivat siis eri liigapisteet, ja kumpi sai enemman ratkesi
 * dokumentti-ID:sta. Yhden pelatun pelin illassa se oli koko tulos.
 *
 * @param standings rankPlayersin palauttama taulukko, jarjestyksessaan.
 * @return sijoitus kullekin alkiolle samassa jarjestyksessa.
 */
export function sharedPositions(standings: PlayerStanding[]): number[] {
  const out: number[] = [];
  standings.forEach((s, i) => {
    const prev = standings[i - 1];
    out.push(prev && prev.wins === s.wins && prev.pts === s.pts ? out[i - 1] : i + 1);
  });
  return out;
}

export function rankPlayers(games: Game[], names: string[] = []): PlayerStanding[] {
  const stats: Record<string, PlayerStanding> = {};
  const ensure = (name: string) => {
    if (!stats[name]) stats[name] = { name, wins: 0, losses: 0, pts: 0, ptsAgainst: 0, played: 0 };
    return stats[name];
  };
  names.forEach(ensure);

  games.forEach(g => {
    const result = gameResult(g);
    if (result === "unplayed") return;
    const s1 = g.score1 as number, s2 = g.score2 as number;
    g.team1.forEach(n => {
      const s = ensure(n);
      s.played++; s.pts += s1; s.ptsAgainst += s2;
      if (result === "team1") s.wins++; else if (result === "team2") s.losses++;
    });
    g.team2.forEach(n => {
      const s = ensure(n);
      s.played++; s.pts += s2; s.ptsAgainst += s1;
      if (result === "team2") s.wins++; else if (result === "team1") s.losses++;
    });
  });

  // Pisteet ratkaisevat, voitot ovat tasatilanteen ratkaisija.
  //
  // Klubin paatos 2.11.2026, ja samalla Americanon oma saanto: parit
  // vaihtuvat joka kierroksella, joten voitto kertoo parionnesta ja
  // tehdyt pisteet omasta pelista. Aiemmin jarjestys oli painvastoin,
  // mika johti siihen etta 1.11. illassa kaikki olivat 3V-3H eika
  // voitoilla erotettu ketaan — koko sijoitus ratkesi silti pisteilla,
  // mutta vasta tasatilanteen ratkaisijana.
  //
  // Seuraus kentalla: romahdukseen ei ole varaa. Samana iltana Tomi
  // havisi viimeisen ottelun 0-11 ja jai kahden pisteen paahan illan
  // voitosta.
  return Object.values(stats).sort((a, b) =>
    b.pts !== a.pts ? b.pts - a.pts : b.wins - a.wins
  );
}

// ── Announcement (clubs/{clubId}/announcements/{id}) ─────────
//
// Klubin oma tiedote: ylläpitäjä kirjoittaa, pelaajat näkevät Uutiset-
// välilehdellä ja saavat pushin (sendAnnouncementPush).
export interface Announcement {
  id: string;
  text: string;
  createdAt: string;
}

// ── News (news/{sourceId}) ───────────────────────────────────
//
// Ulkoisen lähteen uutisotsikot. Ajastettu funktio fetchPickleballNews
// hakee syötteen ja tallentaa otsikot, linkit ja päivämäärät — ei juttuja.
export interface NewsItem {
  title: string;
  link: string;
  publishedAt: string;
  /** Lähteen oma tiivistelmä (RSS description), ilman HTML:ää. */
  summary?: string;
}
export interface NewsDoc {
  source: string;
  sourceUrl: string;
  language: "en" | "fi";
  items: NewsItem[];
  fetchedAt: string;
}
